/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react';
import { FormattedMessage, FormattedHTMLMessage, injectIntl } from 'react-intl';
import PropTypes, { node } from 'prop-types';
import {
  DayPickerRangeController,
  isInclusivelyAfterDay,
  isInclusivelyBeforeDay,
} from 'react-dates';
import classNames from 'classnames';
import moment from 'moment';
import { START_DATE, END_DATE, getDaysFromTimeslots } from '../../util/dates';
import config from '../../config';
import Tooltip from '../Tooltip/Tooltip';
import { ReactComponent as InfoIcon } from '../../assets/icons/info-icon.svg';
import { useIsMobile } from '../../hooks';
import { useDaySize } from './hooks';
import { IconArrowHead, PrimaryButton } from '..';
import css from './DateRangeController.module.css';
import { isOutsideRangeFn } from '../FieldDateRangeInput/DateRangeInput.helpers';

export const HORIZONTAL_ORIENTATION = 'horizontal';
export const ANCHOR_LEFT = 'left';

// IconArrowHead component might not be defined if exposed directly to the file.
// This component is called before IconArrowHead component in components/index.js
const PrevIcon = props => (
  <IconArrowHead {...props} direction="left" rootClassName={css.arrowIcon} />
);
const NextIcon = props => (
  <IconArrowHead {...props} direction="right" rootClassName={css.arrowIcon} />
);

const defaultProps = {
  startDateOffset: undefined,
  endDateOffset: undefined,

  // calendar presentation and interaction related props

  orientation: HORIZONTAL_ORIENTATION,
  verticalHeight: undefined,
  withPortal: false,
  isRTL: false,
  initialVisibleMonth: null,
  firstDayOfWeek: config.i18n.firstDayOfWeek,
  numberOfMonths: 1,
  keepOpenOnDateSelect: false,
  renderCalendarInfo: null,
  hideKeyboardShortcutsPanel: true,

  // navigation related props
  navPrev: <PrevIcon />,
  navNext: <NextIcon />,
  onPrevMonthClick() {},
  onNextMonthClick() {},
  transitionDuration: 200, // milliseconds between next month changes etc.

  renderCalendarDay: undefined, // If undefined, renders react-dates/lib/components/CalendarDay
  // day presentation and interaction related props
  renderDayContents: day => {
    return <span className="renderedDay">{day.format('D')}</span>;
  },
  minimumNights: config.bookingUnitType === 'line-item/night' ? 1 : 0,
  enableOutsideDays: false,
  isDayBlocked: () => false,

  // outside range -><- today ... today+available days -1 -><- outside range
  isOutsideRange: day => {
    const endOfRange = config.dayCountAvailableForBooking - 1;
    return (
      !isInclusivelyAfterDay(day, moment()) ||
      !isInclusivelyBeforeDay(day, moment().add(endOfRange, 'days'))
    );
  },
  isDayHighlighted: () => {},

  // Internationalization props
  // Multilocale support can be achieved with displayFormat like moment.localeData.longDateFormat('L')
  // https://momentjs.com/
  // displayFormat: 'ddd, MMM D',
  monthFormat: 'MMMM YYYY',
  weekDayFormat: 'dd',
  phrases: {}, // Add overwrites to default phrases used by react-dates

  timeSlots: null,
};

const DateRangeController = ({
  rootClassName,
  className,
  value,
  onChange,
  onReset,
  timeSlots,
  unitType,
  isResponsive,
  calendarInfo,
  showClearDates,
  endDateOffset,
  showPreviousDays,
  hasLodging,
  activity,
  intl,
  containerRef,
  ...controllerProps
}) => {
  const { isMobile } = useIsMobile();
  const daySize = useDaySize(containerRef);

  const [startDate, setStartDate] = useState(
    value && value.startDate ? moment(value.startDate) : null
  );
  const [endDate, setEndDate] = useState(value && value.endDate ? moment(value.endDate) : null);
  const [focusedInput, setFocusedInput] = useState(START_DATE);
  const [tooltipText, setTooltipText] = useState(null);
  const [dayTooltip, setDayTooltip] = useState(null);
  const availableDays = getDaysFromTimeslots(timeSlots);
  const [startLodgingDate, setStartLodgingDay] = useState(null);
  const [formDatesState, setFormDatesState] = useState({
    startDate: isMobile && value && value.startDate ? value.startDate : null,
    endDate: isMobile && value && value.endDate ? value.endDate : null,
    startLodgingDate: isMobile && value && value.startLodgingDate ? value.startLodgingDate : null,
  });

  // Adjust minimumNights and maximumNights when lodging is selected
  // (lodging adds an extra night to the booking)
  const getAdjustedNights = () => {
    let { minimumNights, maximumNights } = controllerProps;
    if (startLodgingDate) {
      minimumNights += 1;
      maximumNights += 1;
    }
    return { minimumNights, maximumNights };
  };

  // Apply lodging style to previous day when initial values are loaded
  useEffect(() => {
    if (hasLodging && value) {
      // First check if startLodgingDate is directly provided in the value
      if (value.startLodgingDate) {
        setStartLodgingDay(moment(value.startLodgingDate));
      }
    } else {
      setStartLodgingDay(null);
    }
  }, [hasLodging, value, timeSlots]);

  const handleDatesChange = values => {
    if (isMobile) {
      setFormDatesState(values);
    } else {
      onChange(values);
    }
  };

  const onDatesChange = values => {
    const { startDate: startDateFromValues } = values;
    let { endDate: endDateFromValues } = values;

    if (startDate && startDateFromValues && !endDateOffset) {
      if (!moment(startDate.toDate()).isSame(startDateFromValues.toDate())) {
        endDateFromValues = null;
      }
    }

    // Handle lodging case
    if (hasLodging && startDateFromValues) {
      // In lodging mode, the actual start date to submit is the day before the displayed selected day
      const startLodgingDateValue =
        startLodgingDate && focusedInput !== START_DATE
          ? startLodgingDate
          : moment(startDateFromValues).subtract(1, 'day');

      // Check if the previous day isn't blocked
      const isPreviousDayBlocked =
        timeSlots && !availableDays.includes(startLodgingDateValue.format('YYYY-MM-DD'));

      if (!isPreviousDayBlocked) {
        // Set the lodging day for visual display
        setStartLodgingDay(startLodgingDateValue);

        // Update component state with the visually selected day
        setStartDate(startLodgingDateValue);
        setEndDate(endDateFromValues);

        // Send the actual booking dates to the form (with lodging day as start)
        if (endDateFromValues) {
          handleDatesChange({
            startDate: startLodgingDateValue.toDate(),
            endDate: endDateFromValues.toDate(),
            startLodgingDate: startLodgingDateValue.toDate(),
          });
        }
        return;
      }

      // If the previous day is blocked, reset lodging day
      setStartLodgingDay(null);
    } else {
      // Reset lodging day when not in lodging mode
      setStartLodgingDay(null);
    }

    // Default behavior without lodging or when lodging previous day is blocked
    const start = startDateFromValues ? startDateFromValues.toDate() : null;
    const end = endDateFromValues ? endDateFromValues.toDate() : null;

    setStartDate(startDateFromValues);
    setEndDate(endDateFromValues);

    if (startDateFromValues && endDateFromValues) {
      handleDatesChange({
        startDate: start,
        endDate: end,
        startLodgingDate: hasLodging && startLodgingDate ? startLodgingDate.toDate() : null,
      });
    }
  };

  const onFocusChange = newFocusedInput => {
    // Force the focusedInput to always be truthy so that dates are always selectable
    setFocusedInput(!newFocusedInput ? START_DATE : newFocusedInput);
  };

  const handleReset = (newStartDate, newEndDate) => {
    if (newStartDate && newEndDate) {
      // Set to the provided dates
      setStartDate(moment(newStartDate));
      setEndDate(moment(newEndDate));
      setFocusedInput(START_DATE);
    } else {
      // Clear all date selections
      setStartDate(null);
      setEndDate(null);
      setStartLodgingDay(null);
      setFocusedInput(START_DATE);
      setFormDatesState({
        startDate: null,
        endDate: null,
        startLodgingDate: null,
      });
    }
  };

  const classes = classNames(rootClassName || css.inputRoot, className, {
    [css.inputRootResponsive]: isResponsive,
  });

  let startDateFromForm = value && value.startDate ? moment(value.startDate) : null;
  let endDateFromForm = value && value.endDate ? moment(value.endDate) : null;

  if (isMobile) {
    startDateFromForm = formDatesState.startDate ? moment(formDatesState.startDate) : null;
    endDateFromForm = formDatesState.endDate ? moment(formDatesState.endDate) : null;
  }

  const isSelected = startDate && endDate;

  // Value given by Final Form reflects url params and is valid if both dates are set.
  // If only one date is selected state should be used to get the correct date.
  const startDateUpdated = isSelected ? startDateFromForm : startDate;
  const endDateUpdated = isSelected ? endDateFromForm : endDate;

  const isDayBlocked = day => {
    // If no timeslots are available, don't block any days
    if (!timeSlots) return false;

    const formattedDay = day.format('YYYY-MM-DD');

    // Block days before the current month
    const startOfMonth = moment()
      .startOf('month')
      .format('YYYY-MM-DD');
    if (moment(formattedDay).isBefore(startOfMonth)) {
      return true;
    }

    // Block lodging day if selected
    if (
      startLodgingDate &&
      moment(formattedDay).isSame(startLodgingDate.format('YYYY-MM-DD'), 'day')
    ) {
      return true;
    }

    // Check if the day is available in timeslots
    return !availableDays.includes(formattedDay);
  };

  const isOutsideRange = showPreviousDays
    ? () => false
    : isOutsideRangeFn(
        timeSlots,
        startDateUpdated,
        endDateUpdated,
        focusedInput,
        unitType,
        controllerProps?.maximumNights
      );

  const handleDayClick = day => {
    const { minimumNights, maximumNights } = getAdjustedNights();

    // For lodging scenario, if selecting start date, check if previous day is available
    if (hasLodging && focusedInput === START_DATE) {
      const previousDay = moment(day).subtract(1, 'day');
      const isPreviousDayBlocked =
        timeSlots && !availableDays.includes(previousDay.format('YYYY-MM-DD'));

      if (isPreviousDayBlocked) {
        setDayTooltip(day);
        setTooltipText(intl.formatMessage({ id: 'DateRangeController.previousDayNotAvailable' }));
        return;
      }
    }

    if (focusedInput === END_DATE && minimumNights !== maximumNights) {
      const maximumEndDate = startDateUpdated.clone().add(maximumNights + 1, 'days');
      const minimumEndDate = startDateUpdated.clone().add(minimumNights - 1, 'days');

      // Show min day tooltip
      if (isInclusivelyBeforeDay(day, minimumEndDate)) {
        setDayTooltip(day);
        setTooltipText(
          intl.formatMessage(
            { id: 'DateRangeController.minDaysMessage' },
            { minimumNights: minimumNights + 1 }
          )
        );
      } else if (isInclusivelyAfterDay(day, maximumEndDate)) {
        setDayTooltip(day);
        setTooltipText(
          intl.formatMessage(
            { id: 'DateRangeController.maxDaysMessage' },
            { maximumNights: maximumNights + 1 }
          )
        );
      } else {
        setDayTooltip(null);
        setTooltipText(null);
      }
    }
  };

  const renderDayContents = day => {
    const { maximumNights } = controllerProps;
    const isLodgingDay = startLodgingDate && moment(day).isSame(startLodgingDate, 'day');
    const isEndDay = endDate && moment(day).isSame(endDate, 'day');
    const isStartDay = startDate && moment(day).isSame(startDate, 'day');
    const isPastDay = moment(day).isBefore(moment(), 'day');
    const isFirstHuntActivityDay =
      startLodgingDate &&
      moment(day).isSame(moment(startLodgingDate).add(1, 'day'), 'day') &&
      !isEndDay;

    const dayClassName = classNames('renderedDay', {
      [css.startLodgingDate]: isLodgingDay,
      [css.pastDay]: isPastDay,
      [css.startNoneLodgingDay]: isStartDay && !hasLodging,
      [css.firstHuntActivityDay]: isFirstHuntActivityDay && maximumNights > 1,
    });

    const numberOfDays = startDate && endDate ? moment(endDate).diff(moment(startDate), 'days') : 0;

    return (
      <>
        <span
          data-testId={`calendar-day-${day.format('D')}`}
          className={dayClassName}
          onMouseLeave={() => setDayTooltip(null)}
          onClick={() => handleDayClick(day)}
        >
          {isStartDay && !hasLodging && numberOfDays > 0 && (
            <span className={css.startNoneLodgingDayBackground} />
          )}
          {isLodgingDay && <span className={css.startLodgingDateBackground} />}
          {isEndDay && numberOfDays > 0 && <span className={css.endDayBackground} />}
          {isFirstHuntActivityDay && maximumNights > 1 && (
            <span className={css.firstHuntActivityDayBackground} />
          )}

          <span className={css.dayNumber}>
            {moment(day).isSame(dayTooltip) ? (
              <Tooltip
                placement="top"
                trigger="none"
                tooltipShown
                tooltip={<div>{tooltipText}</div>}
                hideCloseButton
                rootClassName={css.tooltipRoot}
              >
                {day.format('D')}
              </Tooltip>
            ) : (
              day.format('D')
            )}
          </span>

          {isStartDay && !hasLodging && (numberOfDays > 0 || !endDate) && (
            <span className={css.dayInfoLabel}>
              {intl.formatMessage({ id: 'DateRangeController.checkIn' })}
            </span>
          )}
          {isLodgingDay && (
            <span className={css.dayInfoLabel}>
              {intl.formatMessage({ id: 'DateRangeController.checkIn' })}
            </span>
          )}
          {isEndDay && numberOfDays > 0 && (
            <span className={css.dayInfoLabel}>
              {intl.formatMessage({ id: 'DateRangeController.checkOut' })}
            </span>
          )}
        </span>
      </>
    );
  };

  const renderMonthElement = ({ month }) => {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: '8px',
        }}
      >
        <strong>{month.format('MMMM YYYY')}</strong>
      </div>
    );
  };

  const calendarTips = (
    <div className={css.calendarTips}>
      {showClearDates && (
        <a
          className={css.clearDatesLink}
          href="#"
          onClick={e => {
            e.preventDefault();
            handleReset();

            if (onReset) {
              onReset();
            }
          }}
        >
          <FormattedMessage id="DateRangeController.clearDates" />
        </a>
      )}

      {hasLodging && (
        <div className={css.lodgingInfoContainer}>
          <InfoIcon className={css.lodgingInfoIcon} />
          <div
            className={classNames(css.lodgingInfo, {
              [css.lodgingInfoActive]: !!startLodgingDate,
            })}
          >
            {activity === 'hunt' ? (
              <FormattedHTMLMessage id="DateRangeController.lodgingInfoHunt" />
            ) : (
              <FormattedHTMLMessage id="DateRangeController.lodgingInfoGeneral" />
            )}
          </div>
        </div>
      )}
    </div>
  );

  const { minimumNights, maximumNights } = getAdjustedNights();

  return (
    <div className={classes}>
      <div className={css.calendarHeaderWrapper}>
        <div className={css.calendarHeader}>
          <div className={css.calendarHeaderTitle}>
            <FormattedMessage id="DateRangeController.chooseDates" />
          </div>

          {calendarInfo && <div className={css.calendarHeaderSubtitle}>{calendarInfo}</div>}
        </div>
      </div>

      <DayPickerRangeController
        {...controllerProps}
        minimumNights={minimumNights}
        maximumNights={maximumNights}
        daySize={daySize}
        startDate={startDateUpdated}
        endDate={endDateUpdated}
        endDateOffset={endDateOffset}
        onDatesChange={onDatesChange}
        focusedInput={focusedInput}
        onFocusChange={onFocusChange}
        isDayBlocked={isDayBlocked}
        isOutsideRange={isOutsideRange}
        renderDayContents={renderDayContents}
        renderCalendarInfo={() => calendarTips}
        renderMonthElement={renderMonthElement}
      />

      <div className={css.confirmDatesContainer}>
        <PrimaryButton
          disabled={!isSelected}
          isFullWidth
          onClick={() => {
            onChange(formDatesState);
          }}
        >
          <FormattedMessage id="DateRangeController.confirmDates" />
        </PrimaryButton>
      </div>
    </div>
  );
};

DateRangeController.defaultProps = {
  rootClassName: null,
  className: null,

  // I wanted to make this the default but react-dates sometimes
  // renders the month with classes that hide everything for a reason
  // that is not documented so for now it's opt-in
  isResponsive: false,
  calendarInfo: null,
  showClearDates: true,
  hasLodging: false,
  ...defaultProps,
};

const { string, bool } = PropTypes;

DateRangeController.propTypes = {
  rootClassName: string,
  className: string,
  calendarInfo: node,
  isResponsive: bool.isRequired,
  showClearDates: bool,
  hasLodging: bool,
  activity: string.isRequired,
  containerRef: PropTypes.object,
};

export default injectIntl(DateRangeController);
